import {
  Divider,
  MenuItem,
  MenuList,
  Paper,
  Typography
} from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import { InputProps } from '@material-ui/core/Input'
import IconArrowDow from '@material-ui/icons/ArrowDropDown'
import IconArrowUp from '@material-ui/icons/ArrowDropUp'
import React, { ReactNode, useCallback, useRef, useState } from 'react'
import { classes } from '../../../utils/css'
import { debounce } from '../../../utils/timers'
import AppTextField from '../AppTextField'
import styles from './Autocomplete.module.css'

type AppAutocompleteProps = {
  inputProps?: InputProps
  options: (string | { label: ReactNode; value: string })[]
  asAutocompleted?: boolean
  endAdornment?: ReactNode
  label?: string | ReactNode
  variant?: 'filled' | 'outlined' | 'standard'
  className?: string
  onOptionSelected?: (event: number) => void
  position?: 'top' | 'left' | 'right' | 'bottom'
  error?: boolean
  helperText?: string | ReactNode
  loading?: boolean
  stayOpen?: boolean
  onScrollBottom?: () => void
}

function AppAutocomplete(props: AppAutocompleteProps): JSX.Element {
  const [showOptions, setShowOptions] = useState(false)
  const anchorElement = useRef<HTMLDivElement | null>(null)
  const inputElement = useRef<HTMLInputElement | null>(null)

  const debouncedMouseLeave = useCallback(
    debounce(500)(() => {
      !props.stayOpen && setShowOptions(false)
    })(),
    [props.stayOpen]
  )

  return (
    <div className={styles.autocompleteContainer}>
      <AppTextField
        label={props.label}
        ref={anchorElement}
        className={classes(props.className || '')}
        inputProps={{
          ref: inputElement
        }}
        InputProps={{
          ...{
            ...props.inputProps,
            onChange: (event) => {
              setShowOptions(
                !props.asAutocompleted || !!event.currentTarget.value
              )
              props.inputProps?.onChange && props.inputProps.onChange(event)
            },
            endAdornment: (
              <>
                {!props.asAutocompleted
                  ? props.endAdornment ||
                    (showOptions && (
                      <IconArrowUp
                        className={styles.clickable}
                        onClick={() => {
                          setShowOptions(false)
                        }}
                      />
                    )) || (
                      <IconArrowDow
                        className={styles.clickable}
                        onClick={() => {
                          if (props.inputProps?.disabled) return
                          setShowOptions(true)
                        }}
                      />
                    )
                  : undefined}
                {props.loading && <CircularProgress size={20} />}
              </>
            )
          }
        }}
        onFocus={() => {
          setShowOptions(!props.asAutocompleted)
        }}
        variant={props.variant}
        error={props.error}
        helperText={props.helperText}
      />
      {showOptions && props.options.length > 0 ? (
        <Paper
          elevation={6}
          className={styles.optionsContainer}
          onScroll={
            props.onScrollBottom &&
            ((event) => {
              if (
                event.currentTarget.scrollTop >
                event.currentTarget.scrollHeight - 450
              ) {
                props.onScrollBottom && props.onScrollBottom()
              }
            })
          }
          style={{
            minWidth: anchorElement.current?.clientWidth || 0,
            ...(props.position === 'left'
              ? {
                  right: props.asAutocompleted ? '80%' : '100%',
                  transform:
                    props.options.length > 2
                      ? 'translateY(-50%)'
                      : 'translateY(-100%)'
                }
              : props.position === 'right'
              ? {
                  left: props.asAutocompleted ? '80%' : '100%',
                  transform:
                    props.options.length > 2
                      ? 'translateY(-50%)'
                      : 'translateY(-100%)'
                }
              : props.position === 'top'
              ? {
                  bottom: props.asAutocompleted ? '80%' : '100%'
                }
              : {
                  top: props.asAutocompleted
                    ? 'calc(80% + 4px)'
                    : 'calc(100% + 4px)'
                })
          }}
          onMouseLeave={debouncedMouseLeave}
        >
          <MenuList className={styles.menuList}>
            {props.options
              ? props.options.map((option, key) => (
                  <div key={key}>
                    {key > 0 ? (
                      <Divider className={styles.divider} />
                    ) : undefined}
                    <MenuItem
                      className={styles.menuItem}
                      onClick={() => {
                        props.onOptionSelected && props.onOptionSelected(key)
                      }}
                      value={typeof option === 'string' ? option : option.value}
                    >
                      <Typography
                        variant='caption'
                        className={styles.menuItemText}
                      >
                        {typeof option === 'string' ? option : option.label}
                      </Typography>
                    </MenuItem>
                  </div>
                ))
              : undefined}
          </MenuList>
        </Paper>
      ) : undefined}
    </div>
  )
}

export default AppAutocomplete
